import React from 'react'
import {
  isToteRacePushEvent,
  ToteRaceEventPushEvent,
  RaceCompoundKey,
} from 'tabtouch-push-contract'
import { event$ } from '@mobi/utils/awsiot'
import { dayjs } from '@mobi/utils/date'
import { isSameRaceMulti } from '@core/Areas/AccountActivity/Components/Ticket/helpers'
import type { BetLoopShareTicket, BetLoopSharedBet } from '@core/Areas/BetLoop/types'
import { patchFeedDataShareWithBettingClosed } from '@core/Areas/BetLoop/helpers/patchCachedData'

const enum LocalConstants {
  MinutesToBeginListening = 7,
}

export function useRaceClosedTopicSubscription({
  ticket,
  shouldForceDisable,
  postId,
}: {
  ticket: BetLoopShareTicket
  shouldForceDisable: boolean
  postId?: BetLoopSharedBet['postId']
}) {
  const ticketRef = React.useRef<BetLoopShareTicket>(ticket)

  const shouldEnableRaceClosedListener = !!(
    !shouldForceDisable &&
    postId &&
    isRaceAstWithinXMinutes(ticket) &&
    getSingleMatchedRaceInfo(ticket)
  )

  React.useEffect(() => {
    if (!shouldEnableRaceClosedListener) return

    const subscription = event$
      .filter(x => isToteRacePushEvent(x.payload) && x.payload.status === 'raceclosed')
      .subscribe(x => {
        const payload = x.payload as ToteRaceEventPushEvent

        const closedRace = payload.race
        if (!closedRace) return

        const listeningRace = getSingleMatchedRaceInfo(ticketRef.current)
        if (!listeningRace) return

        const isListeningRaceClosed = isRaceClosed(closedRace, listeningRace)
        isListeningRaceClosed && patchFeedDataShareWithBettingClosed(postId)
      })

    return () => subscription.dispose()
  }, [shouldEnableRaceClosedListener, postId])
}

// =============
// Local Helpers
// =============

function getSingleMatchedRaceInfo(ticket: BetLoopShareTicket): RaceInfo | null {
  if (ticket.legs.length !== 1 && !isSameRaceMulti(ticket)) return null

  const { fixtureDate, fixtureId, raceNumber } = ticket.legs[0]
  const hasKeyData = !!(fixtureDate && raceNumber && fixtureId)
  if (!hasKeyData) return null

  return { fixtureId, fixtureDate, raceNumber }
}

function isRaceAstWithinXMinutes(ticket: BetLoopShareTicket): boolean {
  const ast = ticket.legs && ticket.legs[0]?.advertisedStartTime
  if (!ast) return false
  return dayjs(ast).isBefore(dayjs().add(LocalConstants.MinutesToBeginListening, 'minutes'))
}

function isRaceClosed(closedRace: RaceCompoundKey, listeningRace: RaceInfo): boolean {
  const hasMeetingCodeMatch = closedRace.meetingCode === listeningRace.fixtureId
  if (!hasMeetingCodeMatch) return false

  const closedRaceDate =
    typeof closedRace.meetingDate === 'string' &&
    (closedRace.meetingDate as string).toLowerCase().split('t')[0]
  const hasMeetingDateMatch =
    closedRaceDate === listeningRace.fixtureDate.toLowerCase().split('t')[0]
  if (!hasMeetingDateMatch) return false

  return closedRace.raceNumber === listeningRace.raceNumber
}

// =====
// Types
// =====

type RaceInfo = Pick<BetLoopShareTicket['legs'][0], 'fixtureDate' | 'fixtureId' | 'raceNumber'>
